import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import type {
  AppointmentNoteConflictResponse,
  AppointmentNoteResponse,
  AppointmentNoteSaveRequest,
  AppointmentNoteSaveResponse,
} from "../../../../shared/api-types";
import type { RichTextDocument } from "../../../../shared/rich-text-document";
import { ApiRequestError, NetworkRequestError, authenticatedApi } from "@/lib/api";
import {
  AppointmentNoteAutosave,
  type AppointmentNoteAutosaveSnapshot,
  type AppointmentNoteSaveOutcome,
} from "@/lib/appointment-note-autosave";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

export type AppointmentNoteAutosaveUiState = Omit<
  AppointmentNoteAutosaveSnapshot,
  "document"
>;

function autosaveUiState(
  snapshot: AppointmentNoteAutosaveSnapshot
): AppointmentNoteAutosaveUiState {
  return {
    status: snapshot.status,
    dirty: snapshot.dirty,
    conflict: snapshot.conflict,
  };
}

function sameAutosaveUiState(
  current: AppointmentNoteAutosaveUiState,
  next: AppointmentNoteAutosaveUiState
): boolean {
  return (
    current.status === next.status &&
    current.dirty === next.dirty &&
    current.conflict === next.conflict
  );
}

async function saveAppointmentNote(
  getToken: ClerkGetToken,
  appointmentId: string,
  request: AppointmentNoteSaveRequest
): Promise<AppointmentNoteSaveOutcome> {
  try {
    const response = await authenticatedApi<{ data: AppointmentNoteSaveResponse }>(
      getToken,
      `/api/appointments/${encodeURIComponent(appointmentId)}/note`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      }
    );

    return { status: "saved", value: response.data };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 409) {
      const body = error.body as AppointmentNoteConflictResponse;

      if (body?.code === "appointment_note_conflict") {
        return { status: "conflict", current: body.current };
      }
    }

    throw error;
  }
}

function captureAutosaveFailure(error: unknown): void {
  const failure = classifyAutosaveFailure(error);
  Sentry.captureException(new Error("appointment_note_autosave_failed"), {
    tags: {
      ...failure,
      operation: "save",
      source: "client",
      surface: "appointment_notes",
    },
  });
}

export function useAppointmentNoteAutosave({
  appointmentId,
  initial,
  getToken,
}: {
  appointmentId: string;
  initial: AppointmentNoteResponse;
  getToken: ClerkGetToken;
}) {
  return useRevisionedNoteAutosave({
    initial,
    save: (request) => saveAppointmentNote(getToken, appointmentId, request),
    onError: captureAutosaveFailure,
  });
}

/**
 * The React side of one revisioned note editor: the autosave controller, its
 * UI snapshot, and the conflict choices. The Appointment Note and the Patient
 * Quick Note (NO-445) share it; each supplies its own save request and failure
 * capture. The controller is created once, so `save` binds on first render.
 */
export function useRevisionedNoteAutosave({
  initial,
  save,
  onError,
}: {
  initial: AppointmentNoteResponse;
  save: (request: AppointmentNoteSaveRequest) => Promise<AppointmentNoteSaveOutcome>;
  onError: (error: unknown) => void;
}) {
  const [snapshot, setSnapshot] = useState<AppointmentNoteAutosaveUiState>(() => ({
    status: "saved",
    dirty: false,
    conflict: null,
  }));
  const [replacement, setReplacement] = useState<RichTextDocument>();
  const controllerRef = useRef<AppointmentNoteAutosave | null>(null);

  if (!controllerRef.current) {
    controllerRef.current = new AppointmentNoteAutosave(
      initial,
      save,
      (nextSnapshot) => {
        const next = autosaveUiState(nextSnapshot);
        setSnapshot((current) => (sameAutosaveUiState(current, next) ? current : next));
      },
      { onError }
    );
  }

  const controller = controllerRef.current;

  useEffect(() => () => controller.dispose(), [controller]);

  function useLatest(): void {
    const value = controller.useLatest();
    if (value) setReplacement(value);
  }

  return {
    snapshot,
    replacement,
    edit: (document: RichTextDocument) => controller.edit(document),
    retry: () => controller.retry(),
    useLatest,
    saveMine: () => controller.saveMine(),
  };
}

export function classifyAutosaveFailure(error: unknown): {
  code: "network_error" | "http_error" | "unexpected_response";
  status?: number;
} {
  if (error instanceof NetworkRequestError) return { code: "network_error" };

  if (error instanceof ApiRequestError) {
    return { code: "http_error", status: error.status };
  }

  return { code: "unexpected_response" };
}
